import { useEffect, useState } from 'react'
import { X, Download } from 'lucide-react'
import { analyticsService } from '@/services/analytics'

// Import IOSPWAPrompt for iOS
import IOSPWAPrompt from 'react-ios-pwa-prompt'
import { CoreController } from '@/services/coreController'

type Platform = 'ios' | 'android' | 'desktop' | 'unknown'

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>
}

export default function PWAInstallPrompt() {
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null)
  const [showPrompt, setShowPrompt] = useState(false)
  const [platform, setPlatform] = useState<Platform>('unknown')
  const [dismissed, setDismissed] = useState(false)

  // Detect platform and check PWA status
  useEffect(() => {
    const userAgent = window.navigator.userAgent

    // Check if already installed (in standalone mode)
    const isStandalone = window.matchMedia('(display-mode: standalone)').matches ||
                        (window.navigator as any).standalone === true

    if (isStandalone) {
      console.debug('✅ [PWA] Already installed in standalone mode')
      return
    }

    // Check if user previously dismissed
    const wasDismissed = localStorage.getItem('pwa-prompt-dismissed')
    if (wasDismissed) {
      setDismissed(true)
      console.debug('🚫 [PWA] Prompt was previously dismissed')
      return
    }

    // Detect platform
    if (/iPad|iPhone|iPod/.test(userAgent)) {
      setPlatform('ios')
      console.debug('📱 [PWA] iOS platform detected')
    } else if (/Android/.test(userAgent)) {
      setPlatform('android')
      console.debug('🤖 [PWA] Android platform detected')
    } else {
      setPlatform('desktop')
      console.debug('💻 [PWA] Desktop platform detected')

      // Vérifier le statut PWA
      console.debug('🔍 [PWA] Checking PWA status...')

      // Vérifier si le manifest est chargé
      if ('onbeforeinstallprompt' in window) {
        console.debug('✅ [PWA] beforeinstallprompt is supported')
      } else {
        console.warn('❌ [PWA] beforeinstallprompt NOT supported by this browser')
      }

      // Vérifier le service worker
      if ('serviceWorker' in navigator) {
        navigator.serviceWorker.getRegistration().then(registration => {
          if (registration) {
            console.debug('✅ [PWA] Service Worker is active:', registration)
          } else {
            console.warn('❌ [PWA] No Service Worker registration found')
          }
        })
      }

      // Pour desktop, essayer de montrer le prompt après un délai
      // même sans beforeinstallprompt (fallback pour tests)
      const timer = setTimeout(() => {
        if (!dismissed && !showPrompt) {
          console.debug('⚠️ [PWA] No beforeinstallprompt event received, showing manual install prompt')
          setShowPrompt(true)
          analyticsService.trackEvent('pwa_install_prompt_shown', {
            platform: 'desktop',
            method: 'manual-fallback'
          })
        }
      }, 5000)

      return () => clearTimeout(timer)
    }
  }, [])

  // Listen for beforeinstallprompt event (Android/Desktop only)
  useEffect(() => {
    const handleBeforeInstallPrompt = (e: Event) => {
      // Only handle for Android/Desktop
      if (platform === 'ios') {
        return
      }

      console.debug('🎯 [PWA] beforeinstallprompt event fired')
      e.preventDefault()
      const promptEvent = e as BeforeInstallPromptEvent
      setDeferredPrompt(promptEvent)

      // Show prompt after a delay (for Android and Desktop)
      if (platform === 'android' || platform === 'desktop') {
        const timer = setTimeout(() => {
          if (!dismissed) {
            console.debug(`✅ [PWA] Showing ${platform} installation prompt`)
            setShowPrompt(true)
            analyticsService.trackEvent('pwa_install_prompt_shown', {
              platform: platform
            })
          }
        }, 3000)

        return () => clearTimeout(timer)
      }
    }

    const handleAppInstalled = () => {
      console.debug('✅ [PWA] App installed successfully')
      setShowPrompt(false)
      setDeferredPrompt(null)
      analyticsService.trackEvent('pwa_installed', {
        platform: platform
      })
    }

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt)
    window.addEventListener('appinstalled', handleAppInstalled)

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt)
      window.removeEventListener('appinstalled', handleAppInstalled)
    }
  }, [dismissed, platform])

  const handleInstallClick = async () => {
    if (!deferredPrompt) {
      // Fallback: instructions manuelles pour desktop
      console.debug('📥 [PWA] No native prompt, showing manual instructions')
      analyticsService.trackEvent('pwa_install_manual_instructions', { platform })

      // Afficher des instructions dans une alerte ou toast
      alert(`Pour installer l'application sur desktop :\n\n1. Cliquez sur le menu (⋮) dans la barre d'adresse\n2. Sélectionnez "Installer ${CoreController.instance.appSettingResponse.config?.APP_NAME ?? "EasyService"}" ou "Install application"\n\nOu utilisez Chrome/Edge sur un site en HTTPS.`)
      return
    }

    console.debug('📥 [PWA] User clicked install button')
    analyticsService.trackEvent('pwa_install_clicked', { platform })

    deferredPrompt.prompt()
    const { outcome } = await deferredPrompt.userChoice

    if (outcome === 'accepted') {
      console.debug('✅ [PWA] User accepted installation')
      setShowPrompt(false)
      analyticsService.trackEvent('pwa_install_accepted', { platform })
    } else {
      console.debug('❌ [PWA] User dismissed installation')
      analyticsService.trackEvent('pwa_install_dismissed', { platform })
    }

    setDeferredPrompt(null)
  }

  const handleDismiss = () => {
    console.debug('🚫 [PWA] Prompt dismissed by user')
    setShowPrompt(false)
    setDismissed(true)
    localStorage.setItem('pwa-prompt-dismissed', 'true')
    analyticsService.trackEvent('pwa_prompt_dismissed', { platform })
  }

  const handleDontShowAgain = () => {
    handleDismiss()
  }

  // Don't render anything if already dismissed or installed
  if (dismissed) {
    return null
  }

  // iOS: Use react-ios-pwa-prompt component
  if (platform === 'ios') {
    return (
      <IOSPWAPrompt
        delay={5000}
        copyTitle="Ajouter à l'écran d'accueil"
        copyShareStep="1) Appuyez sur Partager"
        copyAddToHomeScreenStep="2) Appuyez sur 'Sur l'écran d'accueil'"
        copyDescription="3) Appuyez sur 'Ajouter'"
        onClose={() => {
          console.debug('🚫 [PWA] iOS prompt closed')
          handleDismiss()
          // Track installation on close since onInstall prop doesn't exist
          analyticsService.trackEvent('pwa_ios_installed', {})
        }}
      />
    )
  }

  // Android: Custom installation prompt
  if (showPrompt && (platform === 'android' || platform === 'desktop')) {
    const platformIcon = platform === 'android' ? '🤖' : '💻';
    const platformText = platform === 'android' ? 'Application Android' : 'Application Desktop';

    return (
      <div className="fixed bottom-4 left-4 right-4 md:left-auto md:right-4 md:w-[400px] z-50 animate-in slide-in-from-bottom-4 duration-300">
        <div className="bg-white rounded-3xl shadow-2xl p-5 border border-gray-100">
          <div className="flex items-start gap-4">
            {/* Logo */}
            <div className="flex-shrink-0 w-16 h-16 bg-white rounded-2xl flex items-center justify-center shadow-lg">
              <img src="/chamlelon_buiness.jpeg" alt={CoreController.instance.appSettingResponse.config?.APP_NAME ?? "EasyService"} className="w-12 h-12" />
            </div>

            <div className="flex-1 min-w-0">
              <div className="flex items-start justify-between mb-2">
                <div>
                  <h3 className="font-bold text-lg text-gray-900">Installer {CoreController.instance.appSettingResponse.config?.APP_NAME ?? "EasyService"}</h3>
                  <p className="text-sm text-gray-500 flex items-center gap-1">
                    <span>{platformIcon}</span>
                    {platformText}
                  </p>
                </div>
                <button
                  onClick={handleDismiss}
                  className="p-2 hover:bg-gray-100 rounded-full transition-colors flex-shrink-0"
                  aria-label="Fermer"
                >
                  <X className="w-4 h-4 text-gray-400" />
                </button>
              </div>

              {/* Benefits */}
              <div className="bg-gradient-to-r from-blue-50 to-purple-50 rounded-xl p-3 mb-3">
                <p className="text-xs text-gray-700">
                  Accès instantané • ⚡ Performance optimale •
                </p>
              </div>

              <p className="text-sm text-gray-600 mb-4">
                Installez l'application pour un accès rapide depuis votre écran d'accueil
              </p>

              {/* Buttons */}
              <div className="flex gap-2">
                <button
                  onClick={handleInstallClick}
                  className="flex-1 flex items-center justify-center gap-2 py-3 px-4 bg-gradient-to-r from-blue-600 to-blue-500 text-white rounded-xl font-semibold hover:shadow-lg transition-all duration-200"
                >
                  <Download className="w-4 h-4" />
                  Installer
                </button>
                <button
                  onClick={handleDontShowAgain}
                  className="py-3 px-4 text-sm text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-xl transition-colors font-medium"
                >
                  Plus tard
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  return null
}
